import { queryOptions, useQuery, type UseBaseQueryOptions } from '@tanstack/react-query'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { fetchKits, fetchKitProducts } from '@/features/shop/services/Kits/fetchers'
import { useDeliveryStores } from '@/features/shop/services/ShoppingStore/hooks'
import { useZip } from '@/features/account/services/Zip/hooks'
import { useIsGuestUser } from '@/features/account/services/User/hooks'

const KitsQuery = 'Kits'
const KitProductsQuery = 'Kit Products'

type KitsQueryParams = {
  store_ids: string | undefined
  zip_code: string | undefined
}

export type KitsQueryKey = readonly [typeof KitsQuery, KitsQueryParams]

export const kitsQueryOptions = (params: KitsQueryParams) =>
  queryOptions({
    queryKey: [KitsQuery, params] as const,
    queryFn: fetchKits,
  })

// storeIdOverride is used in the case where we want to fetch kits for a specific store,
// such as when we're on a retailer kit page. In that case, we want to override the store_id
// param with the storeId from the url rather than using the store_id from the store params,
// which is based on the user's selected store and may not be the same as the store in the url
export const useQueryKits = ({
  storeIdOverride,
  notifyOnChangeProps,
}: {
  storeIdOverride?: number
  notifyOnChangeProps?: UseBaseQueryOptions['notifyOnChangeProps']
} = {}) => {
  // If the user is a guest, we must pass along their inferred zip to fetch delivery stores
  const isGuest = useIsGuestUser()
  const zip = useZip()
  const deliveryStores = useDeliveryStores({ zip: isGuest ? zip : undefined })
  const hasValidStoreIdOverride = storeIdOverride !== undefined && storeIdOverride > 0
  const deliveryStoreIds = deliveryStores.length
    ? deliveryStores.map((s) => s.id).join(',')
    : undefined
  const store_ids = hasValidStoreIdOverride ? String(storeIdOverride) : deliveryStoreIds
  return useQuery({
    ...kitsQueryOptions({ store_ids, zip_code: zip }),
    enabled: Boolean(store_ids && zip),
    notifyOnChangeProps,
  })
}

export type KitProductsParams = {
  kitId: string
  store_id: number | null
  metro_id: number | null
  store_location_id: number | null
  zip_code: string | undefined
}

export type KitProductsQueryKey = readonly [typeof KitProductsQuery, KitProductsParams]

export const kitProductsQueryOptions = (params: KitProductsParams) =>
  queryOptions({
    queryKey: [KitProductsQuery, params] as const,
    queryFn: fetchKitProducts,
  })

export const useQueryKitProducts = (
  kitId: string,
  storeOverride?: {
    store_id: number
    metro_id: number
    store_location_id: number
  },
) => {
  const { store_id, metro_id, store_location_id } = useStoreParams()
  const zip_code = useZip()
  const params: KitProductsParams = {
    kitId,
    store_id: storeOverride?.store_id ?? store_id,
    metro_id: storeOverride?.metro_id ?? metro_id,
    store_location_id: storeOverride?.store_location_id ?? store_location_id,
    zip_code,
  }
  return useQuery({
    ...kitProductsQueryOptions(params),
    enabled: Boolean(
      kitId && params.store_id && params.metro_id && params.store_location_id && zip_code,
    ),
    placeholderData: (previousData) => previousData,
  })
}
